import { Risk } from "@/features/risks/entities/risk/model/risk.types"
import { RISK_HELP_ITEMS } from "../model/risk.help.config"
import { t } from "i18next"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import Chip from "@/components/chip"
import Tooltip from "@/components/tooltip"
import RadioBtn from "@/components/radio-btn"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { getGradientColorAt } from "../utils/getGradientColor.utils"
import RiskTypeChip from "./RiskTypeChip"
import { useAppSelector } from "@/core/root-reducer"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"

interface Props {
  risk: Risk
  onSelect?: (riskId: string) => void
  isSelected?: boolean
  showCheckbox?: boolean
  hideFirstAttribute?: boolean
  containerClassName?: string
}

export default function RiskListItemDetail({
  risk,
  onSelect,
  isSelected = false,
  showCheckbox = false,
  hideFirstAttribute = true,
  containerClassName
}: Props) {
  const optionalRiskAttributes: string[] = useAppSelector(organizationStore.selectors.active)?.optionalRiskAttributes ?? []

  return (
    <ListItem className={`grid grid-cols-[40px,auto] gap-4 h-full ${containerClassName || ""}`}>
      {showCheckbox && (
        <div className="flex items-start">
          <ListCell>
            <RadioBtn onClick={() => onSelect?.(risk.id)} active={isSelected} />
          </ListCell>
        </div>
      )}
      <div className="flex flex-col flex-1">
        {Object.entries(risk.attributes).map(([key, value], index) => {
          if (hideFirstAttribute && index === 0) return null
          const isEmpty = !value || (typeof value === "string" && value.trim() === "")
          if (optionalRiskAttributes.includes(key) && isEmpty) return null
          const isSecond = index === 1
          return (
            <div
              key={key}
              className={isSecond ? "font-bold text-base mb-3 grey-t flex items-start gap-2" : "grey-t mb-2"}>
              {isSecond && (
                <div className="flex-shrink-0">
                  <RiskTypeChip
                    isRps={risk.rps}
                    showCount={false}
                    showLabel={false}
                    disableTooltip
                    isSmall
                    color={risk.valueEffectiveColor ?? undefined}
                  />
                </div>
              )}
              <span>
                <span className={isSecond ? "" : "font-semibold"}>{key}</span> :{" "}
                {value && (typeof value !== "string" || value.trim()) ? value : <span className="red-t italic">{t("non renseigné")}</span>}
              </span>
            </div>
          )
        })}
        <div className="flex-1 flex items-center justify-center overflow-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center content-between">
            {RISK_HELP_ITEMS.map((m, idx) => {
              const label =
                m.id === "f"
                  ? risk.frequencyLabel
                  : m.id === "g"
                    ? risk.severityLabel
                    : m.id === "p"
                      ? risk.preventionLabel
                      : m.id === "rb"
                        ? risk.valueInitialLabel
                        : m.id === "rr"
                          ? risk.valueEffectiveLabel
                          : null

              return (
                <div key={m.id} className="flex flex-col items-center">
                  <h4 className="text-sm text-gray-500 flex items-center justify-center gap-1">
                    {t(m.name)}
                    <Tooltip
                      text={m.descriptions.map((d) => t(d)).join("\n")}
                      position={idx === RISK_HELP_ITEMS.length - 1 ? "left" : "top"}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </Tooltip>
                  </h4>

                  {"colorKey" in m ? (
                    <Chip isOutlineColored={m.id === "rb"} color={risk[m.colorKey]}>
                      {label}
                    </Chip>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      {(() => {
                        const maxValue = Math.max(...risk[`${m.valueKey}Scale`].map((i: any) => i.value))
                        const position = (risk[m.valueKey] - 1) / (maxValue - 1)
                        const isReversed = m.valueKey === "prevention"
                        const color = getGradientColorAt(position, isReversed)
                        return (
                          <span className="text-sm font-semibold" style={{ color }}>
                            {risk[m.valueKey]}/{maxValue}
                          </span>
                        )
                      })()}
                      <span className="text-xs text-gray-600 text-center max-w-[80px]">{label}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </ListItem>
  )
}
